import React from "react";
import { MdSearch, MdFilterList, MdClear } from "react-icons/md";
import {
  getStatusTextColor,
  getStatusBgColor,
  InvoiceStatusType,
  InvoiceStatus,
} from "../utils";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";
interface InvoiceFiltersProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  dateFilter: string;
  onDateFilterChange: (date: string) => void;
  statusCounts: Record<string, number>;
}

const InvoiceFilters: React.FC<InvoiceFiltersProps> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  dateFilter,
  onDateFilterChange,
  statusCounts,
}) => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const statusOptions = [
    {
      value: "all",
      label: language?.groupSubscription?.allStatuses,
      count: Object.values(statusCounts).reduce((a, b) => a + b, 0),
    },
    {
      value: InvoiceStatus.PAID,
      label: language?.groupSubscription?.paid,
      count: statusCounts.paid || 0,
    },
    {
      value: InvoiceStatus.PENDING,
      label: language?.groupSubscription?.pending,
      count: statusCounts.pending || 0,
    },
    {
      value: InvoiceStatus.OVERDUE,
      label: language?.groupSubscription?.overdue,
      count: statusCounts.overdue || 0,
    },
    {
      value: InvoiceStatus.CANCELLED,
      label: language?.groupSubscription?.cancelled,
      count: statusCounts.cancelled || 0,
    },
  ];

  const dateOptions = [
    { value: "all", label: language?.groupSubscription?.allTime },
    { value: "last-30-days", label: language?.groupSubscription?.last30Days },
    { value: "last-90-days", label: language?.groupSubscription?.last90Days },
    { value: "this-year", label: language?.groupSubscription?.thisYear },
    { value: "last-year", label: language?.groupSubscription?.lastYear },
  ];

  const hasActiveFilters = searchTerm || statusFilter !== "all" || dateFilter !== "all";

  const clearAllFilters = () => {
    onSearchChange("");
    onStatusFilterChange("all");
    onDateFilterChange("all");
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:gap-4">
        {/* Search */}
        <div className="w-full">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MdSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={`${language?.groupSubscription?.search} ${language?.groupSubscription?.invoices}...`}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col xs:flex-row gap-2 sm:gap-3">
          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} {option.count > 0 && `(${option.count})`}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <MdFilterList className="h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Date Filter */}
          <div className="relative">
            <select
              value={dateFilter}
              onChange={(e) => onDateFilterChange(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            >
              {dateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <MdFilterList className="h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearAllFilters}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <MdClear className="w-4 h-4 mr-1" />
              {language?.groupSubscription?.clear}
            </button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-sm text-gray-500">
            {language?.groupSubscription?.activeFilters}:
          </span>
          {searchTerm && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {language?.groupSubscription?.search}: "{searchTerm}"
            </span>
          )}
          {statusFilter !== "all" && (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBgColor(
                statusFilter as InvoiceStatusType
              )} ${getStatusTextColor(statusFilter as InvoiceStatusType)}`}
            >
              {language?.groupSubscription?.status}:{" "}
              {statusOptions.find((opt) => opt.value === statusFilter)?.label}
            </span>
          )}
          {dateFilter !== "all" && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              {language?.groupSubscription?.date}:{" "}
              {dateOptions.find((opt) => opt.value === dateFilter)?.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceFilters;
